import { useEffect, useRef, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  BarChart3,
  BrainCircuit,
  Building2,
  CalendarRange,
  ChevronDown,
  KeyRound,
  LayoutDashboard,
  LogOut,
  Menu,
  Palette,
  Phone,
  PhoneCall,
  PhoneForwarded,
  Sheet,
  Users2,
  Volume2,
  Workflow,
  X,
} from "lucide-react";
import { useSession } from "../../hooks/useSession";
import { authApi, isRouteAllowed, restrictedHome } from "../../api/authApi";
import type { SessionDto } from "../../api/authApi";
import { ChangePasswordModal } from "./ChangePasswordModal";
import { portalReportsApi, type ReportListItem } from "../../api/portalReportsApi";
import { pbxExtensionsApi } from "../../api/pbxExtensionsApi";

type NavItem = {
  to: string;
  label: string;
  icon?: React.ReactNode;
};

/** A collapsible group of sub-links (e.g. Reports → Inbound / Outbound). */
type NavGroup = {
  label: string;
  icon: React.ReactNode;
  children: NavItem[];
};

type NavEntry = NavItem | NavGroup;

const isGroup = (e: NavEntry): e is NavGroup => "children" in e;

/**
 * Merges the dynamic reports of the session into the Reports group
 * (created when the nav has none, e.g. admin/manager). The built-in reports
 * sit at sort order 100 (the dbo.PortalReport default): dynamic reports with
 * a lower sort order come before them, the rest after. The API already
 * returns the reports sorted.
 */
const BUILT_IN_REPORTS_SORT_ORDER = 100;

function withDynamicReports(entries: NavEntry[], reports: ReportListItem[]): NavEntry[] {
  if (reports.length === 0) return entries;
  const toItem = (r: ReportListItem): NavItem => ({ to: `/reports/custom/${encodeURIComponent(r.name)}`, label: r.menuLabel });
  const before = reports.filter((r) => (r.sortOrder ?? BUILT_IN_REPORTS_SORT_ORDER) < BUILT_IN_REPORTS_SORT_ORDER).map(toItem);
  const after = reports.filter((r) => (r.sortOrder ?? BUILT_IN_REPORTS_SORT_ORDER) >= BUILT_IN_REPORTS_SORT_ORDER).map(toItem);
  const idx = entries.findIndex((e) => isGroup(e) && e.label === "Reports");
  if (idx >= 0) {
    const g = entries[idx] as NavGroup;
    return entries.map((e, i) => (i === idx ? { ...g, children: [...before, ...g.children, ...after] } : e));
  }
  return [...entries, { label: "Reports", icon: <BarChart3 size={18} />, children: [...before, ...after] }];
}

/** Customer menu narrowed to what a menu-restricted session may open (groups keep only allowed children). */
function filterNav(entries: NavEntry[], session: SessionDto | null | undefined): NavEntry[] {
  return entries
    .map((e) =>
      isGroup(e) ? { ...e, children: e.children.filter((c) => isRouteAllowed(session, c.to)) } : e,
    )
    .filter((e) => (isGroup(e) ? e.children.length > 0 : isRouteAllowed(session, e.to)));
}

// Customer-side menu: items are added as their corresponding backend +
// UI migrations from the legacy portal are completed. Anything that is
// not yet ready stays out of the live navigation so customers don't see
// dead ends.
const CUSTOMER_NAV: NavEntry[] = [
  { to: "/dashboard", label: "Dashboard", icon: <LayoutDashboard size={18} /> },
  { to: "/numbers", label: "Numbers", icon: <Phone size={18} /> },
  { to: "/applications", label: "Applications", icon: <Workflow size={18} /> },
  { to: "/voice", label: "Voice", icon: <Volume2 size={18} /> },
  { to: "/huntgroups", label: "Huntgroups", icon: <Users2 size={18} /> },
  { to: "/destinations", label: "Destinations", icon: <PhoneForwarded size={18} /> },
  { to: "/schedules", label: "Schedules", icon: <CalendarRange size={18} /> },
  { to: "/data", label: "Data Sheets", icon: <Sheet size={18} /> },
  {
    label: "Reports",
    icon: <BarChart3 size={18} />,
    children: [
      { to: "/reports/inbound", label: "Inbound" },
      { to: "/reports/outbound", label: "Outbound" },
      { to: "/reports/cdr", label: "CDR" },
      { to: "/reports/recordings", label: "Recordings" },
    ],
  },
];

// Admin-only menu. Admins don't have a customer context, so showing
// the customer-side items would route to dead ends; instead they get
// only the admin tools they can actually act on.
const ADMIN_NAV: NavEntry[] = [
  { to: "/admin/customers", label: "Customers", icon: <Building2 size={18} /> },
  { to: "/admin/numbers", label: "Numbers", icon: <Phone size={18} /> },
  { to: "/admin/managers", label: "Managers", icon: <Palette size={18} /> },
  { label: "AI/ASR", icon: <BrainCircuit size={18} />, children: [{ to: "/admin/aiagents", label: "Voicebot agents" }, { to: "/admin/recognizers", label: "Recognizers" }] },
  { label: "IPPBX", icon: <PhoneCall size={18} />, children: [{ to: "/ippbx/extensions", label: "Extensions" }, { to: "/ippbx/routing", label: "Routing" }, { to: "/ippbx/banlist", label: "Banlist" }] },
  {
    label: "Other",
    icon: <Menu size={18} />,
    children: [
      { to: "/admin/reports", label: "Report builder" },
      { to: "/admin/permission-groups", label: "Permission groups" },
      // TEMPORARY: remove once the Fonix history has been migrated.
      { to: "/admin/fonix-entry-import", label: "Fonix entry import (temp)" },
    ],
  },
];

// Menu for a login restricted to the Recordings search only.
const RECORDINGS_ONLY_NAV: NavEntry[] = [
  { to: "/reports/recordings", label: "Recordings", icon: <BarChart3 size={18} /> },
];

// Manager-side menu: a manager is scoped to their own customers. Numbers
// stay reachable via the customer detail; the top-level Numbers item is
// admin-only for now.
const MANAGER_NAV: NavEntry[] = [
  { to: "/admin/customers", label: "Customers", icon: <Building2 size={18} /> },
  { label: "IPPBX", icon: <PhoneCall size={18} />, children: [{ to: "/ippbx/extensions", label: "Extensions" }, { to: "/ippbx/routing", label: "Routing" }, { to: "/ippbx/banlist", label: "Banlist" }] },
];

/**
 * Left-hand navigation for authenticated users: a tenant/role selector
 * on top, followed by a vertical list of primary sections and a Sign
 * out action at the bottom (above the contact-support footer line).
 */
export function Sidebar() {
  const { session, refresh } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  /** Groups the user opened by hand; a group also auto-opens while one of
   *  its children is the active route. */
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});
  /** Mobile only: the nav is collapsed behind a hamburger and closes on
   *  every navigation. On desktop the toggle is hidden and the nav is
   *  always shown (see the @media rules in app.css). */
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);
  /** Dynamic reports for this session (customer: assigned; manager/admin: by audience). */
  const [dynamicReports, setDynamicReports] = useState<ReportListItem[]>([]);
  const sessionKey = `${session?.kind ?? ""}:${session?.customerId ?? 0}:${session?.managerId ?? 0}:${session?.userAuthId ?? ""}`;
  useEffect(() => {
    let active = true;
    setDynamicReports([]);
    if (!session?.isAuthenticated || restrictedHome(session)) return;
    portalReportsApi
      .list()
      .then((r) => active && setDynamicReports(r))
      .catch(() => active && setDynamicReports([]));
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionKey]);
  // A manager only gets the IPPBX group when at least one of their customers has
  // IPPBX enabled (admins always see it). Unknown (null) hides nothing yet.
  const [managerHasPbx, setManagerHasPbx] = useState<boolean | null>(null);
  useEffect(() => {
    let active = true;
    setManagerHasPbx(null);
    if (!session?.isAuthenticated || session.kind !== "Manager") return;
    pbxExtensionsApi
      .customers()
      .then((rows) => active && setManagerHasPbx(rows.length > 0))
      .catch(() => active && setManagerHasPbx(null));
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionKey]);
  const managerNav = managerHasPbx === false ? MANAGER_NAV.filter((e) => !(isGroup(e) && e.label === "IPPBX")) : MANAGER_NAV;
  /** The account menu under the user button (Sign out lives here). */
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  /** Impersonation runs under the customer's credentials, so there is no own password to change. */
  const canChangePassword = !session?.originalKind;
  const userMenuRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!userMenuOpen) return;
    const onDown = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) setUserMenuOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setUserMenuOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [userMenuOpen]);
  const kindLabel =
    session?.kind === "Customer"
      ? "Customer"
      : session?.kind === "User"
      ? "User"
      : session?.kind === "Admin"
      ? "Admin"
      : session?.kind === "Manager"
      ? "Manager"
      : "Profile";

  const handleSignOut = async () => {
    try {
      await authApi.logout();
    } finally {
      await refresh();
      navigate("/");
    }
  };

  return (
    <aside className="sidebar" aria-label="Main navigation">
      <div className="sidebar-head">
        <div className="sidebar-user-wrap" ref={userMenuRef}>
          <button
            type="button"
            className={`sidebar-user${userMenuOpen ? " is-open" : ""}`}
            aria-haspopup="menu"
            aria-expanded={userMenuOpen}
            onClick={() => setUserMenuOpen((o) => !o)}
          >
            <div className="sidebar-user-avatar" aria-hidden="true">
              {(session?.displayName ?? "?").charAt(0).toUpperCase()}
            </div>
            <div className="sidebar-user-meta">
              <span className="sidebar-user-role">{kindLabel}</span>
              <span className="sidebar-user-name">
                {session?.displayName ?? "Signed in"}
              </span>
            </div>
            <ChevronDown size={16} aria-hidden="true" className={`sidebar-user-chevron${userMenuOpen ? " is-open" : ""}`} />
          </button>
          {userMenuOpen && (
            <div className="sidebar-user-menu" role="menu">
              {canChangePassword && (
                <button type="button" role="menuitem" className="sidebar-user-menu-item" onClick={() => { setUserMenuOpen(false); setChangePasswordOpen(true); }}>
                  <KeyRound size={16} aria-hidden="true" /> Change password
                </button>
              )}
              <button type="button" role="menuitem" className="sidebar-user-menu-item" onClick={() => { setUserMenuOpen(false); void handleSignOut(); }}>
                <LogOut size={16} aria-hidden="true" /> Sign out
              </button>
            </div>
          )}
          <ChangePasswordModal open={changePasswordOpen} onClose={() => setChangePasswordOpen(false)} />
        </div>
        <button
          type="button"
          className="sidebar-hamburger"
          aria-label={mobileOpen ? "Close menu" : "Open menu"}
          aria-expanded={mobileOpen}
          aria-controls="sidebar-nav"
          onClick={() => setMobileOpen((o) => !o)}
        >
          {mobileOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      <nav id="sidebar-nav" className={`sidebar-nav${mobileOpen ? " is-open" : ""}`}>
        {(restrictedHome(session)
          ? RECORDINGS_ONLY_NAV
          : session?.kind === "Admin"
          ? withDynamicReports(ADMIN_NAV, dynamicReports)
          : session?.kind === "Manager"
          ? withDynamicReports(managerNav, dynamicReports)
          : filterNav(withDynamicReports(CUSTOMER_NAV, dynamicReports), session)
        ).map((entry) => {
          if (isGroup(entry)) {
            const childActive = entry.children.some((c) => location.pathname.startsWith(c.to));
            const open = openGroups[entry.label] ?? childActive;
            return (
              <div key={entry.label} className="sidebar-group">
                <button
                  type="button"
                  className={`sidebar-link sidebar-group-toggle${childActive ? " is-child-active" : ""}`}
                  aria-expanded={open}
                  onClick={() => setOpenGroups({ ...openGroups, [entry.label]: !open })}
                >
                  <span className="sidebar-link-icon" aria-hidden="true">
                    {entry.icon}
                  </span>
                  <span>{entry.label}</span>
                  <ChevronDown
                    size={16}
                    aria-hidden="true"
                    className={`sidebar-group-chevron${open ? " is-open" : ""}`}
                  />
                </button>
                {open &&
                  entry.children.map((child) => (
                    <NavLink
                      key={child.to}
                      to={child.to}
                      className={({ isActive }) =>
                        `sidebar-link sidebar-sublink${isActive ? " is-active" : ""}`
                      }
                    >
                      <span>{child.label}</span>
                    </NavLink>
                  ))}
              </div>
            );
          }
          return (
            <NavLink
              key={entry.to}
              to={entry.to}
              end={entry.to === "/dashboard"}
              className={({ isActive }) =>
                `sidebar-link${isActive ? " is-active" : ""}`
              }
            >
              <span className="sidebar-link-icon" aria-hidden="true">
                {entry.icon}
              </span>
              <span>{entry.label}</span>
            </NavLink>
          );
        })}
      </nav>

    </aside>
  );
}
